import { InputGroup } from '@chakra-ui/react'
import { Box, Flex, Icon, Input, Text } from '@chakra-ui/react'
import React, { useMemo, useRef, useState } from 'react'

import { Checkbox } from '@mangovoice/mango-ui'

import { useReportingI18n } from '../../i18n'
import { useOutsideClick } from '../hooks/useOutsideClick'
import { DownChevronIcon, SearchIcon, UpChevronIcon } from '../icons'
import { useDropdownStyles } from '../styles/Dropdown.style'
import { castRef } from '../utils'
import { Menu, MenuOptions } from './Menu'
import { MenuItem } from './MenuItem'

export type MultiSelectItem<TValue> = MenuItem<TValue> & {
  searchLabel?: string
  label: string
}

type DropdownMultiSelectProps<TValue extends string | number> = {
  placeholder?: string
  selectedValues: TValue[]
  items: MultiSelectItem<TValue>[]
  onSelectionChange: (values: TValue[]) => void
  onScrollEndReached?: () => void
  menuOptions?: MenuOptions
  searchable?: boolean
  maxSelection?: number
}

export const DropdownMultiSelect = <TValue extends string | number>({
  placeholder = 'Select items',
  selectedValues,
  items = [],
  onSelectionChange,
  onScrollEndReached,
  menuOptions,
  searchable,
  maxSelection,
}: DropdownMultiSelectProps<TValue>) => {
  const { text } = useReportingI18n()
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const styles = useDropdownStyles()

  useOutsideClick({
    ref: castRef(containerRef),
    handler: () => {
      if (isOpen) setIsOpen(false)
    },
  })

  const isSearchable = searchable !== undefined ? searchable : items.length > 10

  const handleToggleItem = (value: TValue) => {
    const isCurrentlySelected = selectedValues.includes(value)

    if (isCurrentlySelected) {
      onSelectionChange(selectedValues.filter((v) => v !== value))
    } else {
      if (maxSelection && selectedValues.length >= maxSelection) {
        return
      }
      onSelectionChange([...selectedValues, value])
    }
  }

  const handleMenuToggle = () => {
    setIsOpen((prev) => !prev)
    setSearchTerm('')
  }

  const filteredItems = useMemo(() => {
    if (!isSearchable || searchTerm.trim() === '') {
      return items
    }
    return items.filter((item) => {
      const searchableText = item.searchLabel || item.label
      return searchableText.toLowerCase().includes(searchTerm.toLowerCase())
    })
  }, [items, searchTerm, isSearchable])

  const placeholderText = useMemo(() => {
    return <Text css={styles.placeholderText}>{placeholder}</Text>
  }, [placeholder, styles.placeholderText])

  const selectedText = useMemo(() => {
    if (selectedValues.length === 0) {
      return placeholderText
    }

    if (selectedValues.length === 1) {
      const selectedItem = items.find(
        (item) => item.value === selectedValues[0],
      )
      return (
        <Text lineClamp={1} css={styles.defaultText}>
          {selectedItem?.label || selectedValues[0]}
        </Text>
      )
    }

    return (
      <Text lineClamp={1} css={styles.defaultText}>
        {selectedValues.length} selected
      </Text>
    )
  }, [selectedValues, items, placeholderText, styles.defaultText])

  const wrappedItems = useMemo(() => {
    return filteredItems.map((item) => {
      const isSelected = selectedValues.includes(item.value)

      return {
        value: item.value,
        content: (
          <Flex
            key={item.value}
            alignItems="center"
            gap="0.5rem"
            width="100%"
            padding="0.5rem 0.75rem"
            cursor="pointer"
            _hover={{
              bg: 'surface.hover',
            }}
            onClick={(e) => {
              e.stopPropagation()
              handleToggleItem(item.value)
            }}
          >
            <Checkbox checked={isSelected} pointerEvents="none" />
            <Box flex="1" minWidth={0}>
              {item.content || item.label}
            </Box>
          </Flex>
        ),
      }
    })
  }, [filteredItems, selectedValues])

  return (
    <Flex ref={containerRef} css={styles.container}>
      <Flex
        ref={dropdownRef}
        css={styles.menuButton}
        onClick={handleMenuToggle}
      >
        <Box css={{ px: 1 }}>{selectedText}</Box>
        <Icon css={styles.icon}>
          {isOpen ? <UpChevronIcon /> : <DownChevronIcon />}
        </Icon>
      </Flex>
      {isOpen && (
        <Menu
          header={
            isSearchable ? (
              <InputGroup
                endElement={
                  <Icon color="border.muted" boxSize={4}>
                    <SearchIcon />
                  </Icon>
                }
              >
                <Input
                  size="sm"
                  placeholder={text('dropdownSelect.placeholder', 'Search...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            ) : undefined
          }
          anchorRef={castRef(dropdownRef)}
          items={wrappedItems}
          onSelect={() => {}}
          onScrollEndReached={onScrollEndReached}
          options={menuOptions}
        />
      )}
    </Flex>
  )
}
